import { memo } from 'react';
import { Link, Navigate } from 'react-router-dom';

// ── TO RE-ENABLE SIGNUPS: change false → true ─────────────────────────────────
const SIGNUPS_OPEN = false;
import { useAuth } from '../hooks/useAuth';
import { useLoginForm } from '../hooks/useLoginForm';

// ── Field ─────────────────────────────────────────────────────────────────────

interface FieldProps {
  id: string;
  name: string;
  label: string;
  type?: string;
  autoComplete?: string;
  value: string;
  error?: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur: React.ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
}

const Field = memo(function Field({
  id, name, label, type = 'text', autoComplete, value, error, onChange, onBlur, disabled,
}: FieldProps) {
  return (
    <div className="form-field">
      <label className="form-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        name={name}
        className={`form-input${error ? ' form-input--error' : ''}`}
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        aria-describedby={error ? `${id}-error` : undefined}
        aria-invalid={!!error}
      />
      {error && (
        <span id={`${id}-error`} className="form-error" role="alert">{error}</span>
      )}
    </div>
  );
});

// ── Page ──────────────────────────────────────────────────────────────────────

export default function LoginPage() {
  const { isAuthed } = useAuth();
  const {
    fields, errors, serverError, loading,
    handleChange, handleBlur, handleSubmit,
  } = useLoginForm();

  if (isAuthed) return <Navigate to="/practice" replace />;

  return (
    <div className="auth-page">
      <div className="auth-header">
        <h1 className="auth-title">sign in</h1>
        <p className="auth-subtitle">Welcome back.</p>
      </div>

      <form
        className="auth-form"
        onSubmit={handleSubmit}
        noValidate
        aria-label="Sign in"
      >
        <Field
          id="email"
          name="email"
          label="email"
          type="email"
          autoComplete="email"
          value={fields.email}
          error={errors.email}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={loading}
        />

        <Field
          id="password"
          name="password"
          label="password"
          type="password"
          autoComplete="current-password"
          value={fields.password}
          error={errors.password}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={loading}
        />

        {serverError && (
          <div className="form-server-error" role="alert">
            {serverError}
          </div>
        )}

        <button
          className="btn btn--primary auth-submit-btn"
          type="submit"
          disabled={loading}
          aria-busy={loading}
        >
          {loading ? (
            <span className="auth-btn-loading">
              <span className="auth-spinner" />
              signing in…
            </span>
          ) : (
            'sign in'
          )}
        </button>
      </form>

      <p className="auth-footer">
        <Link to="/auth/forgot-password">forgot password?</Link>
      </p>
      {SIGNUPS_OPEN && (
        <p className="auth-footer">
          no account? <Link to="/register">create one free</Link>
        </p>
      )}
    </div>
  );
}
